import React, { useEffect } from 'react';
import './webrosterLanding.css';

const FEATURES = [
  { icon: 'fa-calendar', title: 'Smart Scheduling', desc: 'Build balanced shift schedules in minutes. Drag-and-drop planning with conflict detection and demand-based optimization.' },
  { icon: 'fa-clock-o', title: 'Real-Time Attendance', desc: 'See who is on site right now. Live dashboard shows clock-ins, absences, and late arrivals the moment they happen.' },
  { icon: 'fa-hand-o-up', title: 'Biometric Integration', desc: 'Connect fingerprint, face, or RFID readers directly. Accurate, automated time capture with zero manual entry.' },
  { icon: 'fa-bar-chart', title: 'Analytics & Reports', desc: 'Turn workforce data into decisions. Overtime trends, attendance patterns, cost analysis all in one place.' },
  { icon: 'fa-mobile', title: 'Mobile Ready', desc: 'Full platform access from any device. Managers and employees can check schedules, approve requests, and more on the go.' },
  { icon: 'fa-shield', title: 'Role-Based Access', desc: 'Control exactly who sees and edits what. Set granular permissions by role, department, or location.' },
];

const INDUSTRIES = [
  { icon: 'fa-plane',        label: 'Airlines',      desc: 'Ground crews, cabin staff, check-in agents — complex rotations across terminals and time zones.' },
  { icon: 'fa-shopping-bag', label: 'Retail',        desc: 'Multi-branch scheduling, seasonal peaks, and cashier rotations across any number of locations.' },
  { icon: 'fa-building',     label: 'Hospitality',   desc: 'Housekeeping, F&B, and front desk — 24/7 operations with zero room for coverage gaps.' },
  { icon: 'fa-truck',        label: 'Logistics',     desc: 'Warehouse shifts, route dispatch, and loading crews where timing directly impacts the supply chain.' },
  { icon: 'fa-flask',        label: 'Healthcare',    desc: 'Nurses, technicians, and support staff across wards and clinics with strict compliance requirements.' },
  { icon: 'fa-industry',     label: 'Manufacturing', desc: 'Production lines, quality control, and maintenance teams running three shifts around the clock.' },
];

const STEPS = [
  { n: '01', title: 'Set up your structure', desc: 'Add your departments, locations, and employee profiles. Import from a spreadsheet or set up manually in minutes.' },
  { n: '02', title: 'Plan and assign shifts', desc: 'Use smart scheduling to assign shifts, handle rotations, and auto-fill open slots based on availability.' },
  { n: '03', title: 'Track, analyze, optimize', desc: 'Monitor attendance in real time, run payroll-ready reports, and continuously improve workforce efficiency.' },
];

const ROSTER = [
  { name: 'Ana M.', shift: 'Morning', status: 'in' },
  { name: 'Carlos R.', shift: 'Afternoon', status: 'out' },
  { name: 'Luis H.', shift: 'Night', status: 'in' },
  { name: 'Sofia T.', shift: 'Morning', status: 'late' },
];

const BARS = [80, 92, 75, 95, 88, 60, 72];
const DAYS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

const DashboardMock = () => (
  <div className="wr-dash">
    <div className="wr-dash-topbar">
      <div className="wr-dash-dot" />
      <div className="wr-dash-dot" />
      <div className="wr-dash-dot" />
      <span className="wr-dash-title">Webroster &mdash; Dashboard</span>
    </div>
    <div className="wr-dash-body">
      <div className="wr-dash-sidebar">
        {['Overview', 'Schedules', 'Attendance', 'Reports', 'Settings'].map((s) => (
          <div key={s} className={`wr-dash-navitem${s === 'Attendance' ? ' is-active' : ''}`}>{s}</div>
        ))}
      </div>
      <div className="wr-dash-main">
        <div className="wr-dash-stats">
          {[['42', 'On Duty'], ['3', 'Absent'], ['5', 'Late'], ['8h', 'Overtime']].map(([v, l]) => (
            <div key={l} className="wr-dash-stat">
              <span className="wr-dash-stat-val">{v}</span>
              <span className="wr-dash-stat-label">{l}</span>
            </div>
          ))}
        </div>
        <div className="wr-dash-chart">
          <div className="wr-dash-chart-label">Attendance This Week</div>
          <div className="wr-dash-bars">
            {BARS.map((h, i) => (
              <div key={i} className="wr-dash-bar" style={{ height: `${h}%` }} />
            ))}
          </div>
          <div className="wr-dash-days">
            {DAYS.map((d, i) => <span key={i}>{d}</span>)}
          </div>
        </div>
        <div className="wr-dash-roster">
          {ROSTER.map((e) => (
            <div key={e.name} className="wr-dash-row">
              <div className="wr-dash-avatar" />
              <div className="wr-dash-empinfo">
                <span>{e.name}</span>
                <small>{e.shift}</small>
              </div>
              <div className={`wr-dash-badge wr-dash-badge--${e.status}`}>
                {e.status === 'in' ? 'In' : e.status === 'late' ? 'Late' : 'Out'}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

export const WebrosterLanding = () => {
  useEffect(() => {
    const prev = document.title;
    document.title = 'Webroster | Workforce Management Platform';
    return () => { document.title = prev; };
  }, []);

  return (
    <div className="wr-page">

      {/* ── Nav ── */}
      <nav className="wr-nav">
        <div className="wr-nav-inner">
          <a href="/webroster" className="wr-nav-brand">
            <img src="/img/logo-webroster.png" alt="Webroster" />
          </a>
          <div className="wr-nav-links">
            <a href="#wr-features">Features</a>
            <a href="#wr-industries">Industries</a>
            <a href="#wr-how">How it works</a>
            <a href="#wr-cta" className="wr-nav-cta">Request Demo</a>
          </div>
          <a href="/" className="wr-back">&#8592; Mindware</a>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="wr-hero">
        <div className="wr-hero-bg" />
        <div className="wr-hero-grid" />
        <div className="wr-wrap wr-hero-inner">
          <div className="wr-hero-copy">
            <span className="wr-eyebrow">Workforce Management Platform</span>
            <h1>
              Workforce management<br />
              <span className="wr-grad">that actually works.</span>
            </h1>
            <p>
              Webroster is the all-in-one platform for scheduling, attendance
              tracking, and biometric control. Built for operations teams that
              need reliability, not just software.
            </p>
            <div className="wr-hero-actions">
              <a href="#wr-cta" className="wr-btn-primary">Request a Demo</a>
              <a href="#wr-features" className="wr-btn-ghost">See Features</a>
            </div>
          </div>
          <div className="wr-hero-visual">
            <DashboardMock />
          </div>
        </div>
      </section>

      {/* ── Trusted by airlines ── */}
      <div className="wr-proof">
        <div className="wr-wrap wr-proof-inner">
          <span className="wr-proof-label">Trusted by</span>
          <div className="wr-proof-sep" />
          <div className="wr-airline wr-airline--aeromexico">
            <i className="fa fa-plane" />
            <div>
              <strong>Aero</strong><em>méx</em><strong>ico</strong>
            </div>
          </div>
          <div className="wr-proof-sep" />
          <div className="wr-airline wr-airline--volaris">
            <i className="fa fa-plane" />
            <div>
              <strong>Volaris</strong>
            </div>
          </div>
          <div className="wr-proof-sep" />
          <div className="wr-airline wr-airline--viva">
            <i className="fa fa-plane" />
            <div>
              <strong>Viva</strong><em>Aerobus</em>
            </div>
          </div>
        </div>
      </div>

      {/* ── Features ── */}
      <section id="wr-features" className="wr-section wr-features-section">
        <div className="wr-wrap">
          <div className="wr-section-head">
            <span className="wr-label">Features</span>
            <h2>Everything you need to run your workforce</h2>
            <p>From the first clock-in to the final payroll report &mdash; Webroster has every piece.</p>
          </div>
          <div className="wr-grid">
            {FEATURES.map((f, i) => (
              <div key={i} className="wr-feat-card">
                <i className={`fa ${f.icon} wr-feat-icon`} />
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Industries ── */}
      <section id="wr-industries" className="wr-section wr-industries-section">
        <div className="wr-wrap">
          <div className="wr-section-head">
            <span className="wr-label">Industries</span>
            <h2>Built for operations that never stop</h2>
            <p>
              Wherever people work in shifts, rotations, or time-critical schedules,
              Webroster fits the operation.
            </p>
          </div>
          <div className="wr-ind-grid">
            {INDUSTRIES.map((ind, i) => (
              <div key={i} className="wr-ind-card">
                <div className="wr-ind-icon">
                  <i className={`fa ${ind.icon}`} />
                </div>
                <h3>{ind.label}</h3>
                <p>{ind.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ── */}
      <section id="wr-how" className="wr-section wr-how-section">
        <div className="wr-wrap">
          <div className="wr-section-head">
            <span className="wr-label">Process</span>
            <h2>Up and running in days, not months</h2>
            <p>Most teams are fully operational within a week of onboarding.</p>
          </div>
          <div className="wr-steps">
            {STEPS.map((s, i) => (
              <React.Fragment key={i}>
                <div className="wr-step">
                  <div className="wr-step-num">{s.n}</div>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                </div>
                {i < STEPS.length - 1 && <div className="wr-step-arrow">&#8594;</div>}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section id="wr-cta" className="wr-section wr-cta-section">
        <div className="wr-wrap wr-cta-inner">
          <h2>Ready to optimize your workforce?</h2>
          <p>Contact us for a free demo tailored to your operation.</p>
          <a href="mailto:contact@mindware.com.mx" className="wr-btn-primary wr-btn-lg">
            Request a Demo &nbsp;<i className="fa fa-arrow-right" />
          </a>
          <div className="wr-cta-sub">
            <a href="mailto:contact@mindware.com.mx">contact@mindware.com.mx</a>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="wr-footer">
        <div className="wr-wrap wr-footer-inner">
          <span>Webroster by <a href="/">Mindware</a></span>
          <a href="/">&#8592; Back to Mindware</a>
        </div>
      </footer>

    </div>
  );
};
